import type {
	CustomMonitorContent,
	MonitorCadence,
	MonitorSource,
} from '../../../../../bridge/types/artifact.ts';

// Plain-words label for each sensing tool, so the config form never shows the
// internal tool name to the user.
export const SOURCE_TOOL_LABELS: Record<MonitorSource['tool'], string> = {
	scrape_website: 'Website page',
	web_search: 'Web search',
	reddit_top_posts: 'Subreddit',
	scrape_instagram: 'Instagram profile',
};

export const CADENCE_PRESETS: {
	value: MonitorCadence['preset'];
	label: string;
}[] = [
	{ value: 'hourly', label: 'Every hour' },
	{ value: 'twice_daily', label: 'Twice a day' },
	{ value: 'daily', label: 'Every day' },
	{ value: 'weekdays', label: 'Weekdays' },
	{ value: 'weekly', label: 'Weekly' },
];

// The "hourly" preset fires on an interval, not at a fixed minute, so the time
// picker is hidden and `at` is ignored for it.
export const cadenceUsesTime = (preset: MonitorCadence['preset']): boolean =>
	preset !== 'hourly';

// "13:05" -> "1:05 PM". Falls back to the raw string if it isn't HH:MM.
export const formatClockTime = (at: string | null | undefined): string => {
	if (!at) return '';
	const match = /^(\d{1,2}):(\d{2})$/.exec(at.trim());
	if (!match) return at;
	const hour = Number(match[1]);
	const minute = match[2];
	if (hour < 0 || hour > 23) return at;
	const period = hour < 12 ? 'AM' : 'PM';
	const hour12 = hour % 12 === 0 ? 12 : hour % 12;
	return `${hour12}:${minute} ${period}`;
};

// Adds 12 hours to an HH:MM time (wrapping past midnight) — the second fire time
// of the twice-daily cadence.
export const addTwelveHours = (at: string | null | undefined): string => {
	if (!at) return '';
	const match = /^(\d{1,2}):(\d{2})$/.exec(at.trim());
	if (!match) return '';
	const hour = (Number(match[1]) + 12) % 24;
	return `${String(hour).padStart(2, '0')}:${match[2]}`;
};

// "Twice a day at 9:00 AM & 9:00 PM" — the human phrasing of both fire times.
export const twiceDailyTimesLabel = (at: string | null | undefined): string => {
	const first = formatClockTime(at);
	const second = formatClockTime(addTwelveHours(at));
	if (!first || !second) return '';
	return `${first} & ${second}`;
};

// The cadence half of the summary sentence, e.g. "Every day at 9:00 AM".
// Tolerates a missing cadence: this renders a live preview of a document that may
// still be arriving, and its Python twin degrades rather than raising.
export const cadencePhrase = (
	cadence: MonitorCadence | null | undefined,
): string => {
	const time = formatClockTime(cadence?.at);
	const preset = cadence?.preset;
	switch (preset) {
		case 'hourly':
			return 'Every hour';
		case 'twice_daily': {
			// Both fire times, because the schedule really registers both. If the
			// second cannot be derived, name the first rather than a dangling "&".
			const second = formatClockTime(addTwelveHours(cadence?.at));
			if (!time) return 'Twice a day';
			return second
				? `Twice a day at ${time} & ${second}`
				: `Twice a day at ${time}`;
		}
		case 'weekdays':
			return time ? `On weekdays at ${time}` : 'On weekdays';
		case 'weekly':
			return time ? `Every week at ${time}` : 'Every week';
		case 'daily':
		case undefined:
			return time ? `Every day at ${time}` : 'Every day';
		default: {
			// Exhaustiveness check: a new MonitorCadence['preset'] member with no
			// case above fails here at compile time instead of silently falling
			// through to "Every day", the way the merged daily/default case used to.
			const _exhaustive: never = preset;
			return _exhaustive;
		}
	}
};

const outputClause = (mode: CustomMonitorContent['output_mode']): string =>
	mode === 'insight' ? 'add it to my insights' : 'message me in chat';

// The backend's `sources must have between 1 and 3 entries` bound.
export const MAX_MONITOR_SOURCES = 3;

// Python's str.strip() and JS's String.trim() disagree: Python also strips
// U+001C-1F and U+0085, JS also strips U+FEFF. Targets and instructions are
// routinely pasted from web pages, so a BOM or a separator character would make
// the preview and the persisted sentence differ. Both twins trim the UNION.
// Built via String.raw + RegExp so the control characters are not written as a
// regex literal (eslint's no-control-regex flags that, reasonably).
const EXTRA_TRIM_CLASS = '[\\u001c-\\u001f\\u0085\\ufeff]';
const EXTRA_TRIM = new RegExp(
	`^${EXTRA_TRIM_CLASS}+|${EXTRA_TRIM_CLASS}+$`,
	'g',
);
export const monitorTrim = (value: string | null | undefined): string =>
	(value ?? '').replace(EXTRA_TRIM, '').trim();

// Covers everything CustomMonitorContent.validate_monitor asks for — 1–3
// sources, EVERY target non-empty, and a non-empty instruction — and then asks
// for one thing more: no target may carry a `problem` from checkMonitorTarget.
// So this gate is STRICTER than the backend, on purpose. The invariant that
// matters is one-directional: the UI must never submit a document the backend
// refuses. A stricter gate cannot break that direction, and it is the only way
// to stop a target the sensing tool can never use. Both the draft panel's "Start
// monitoring" and the active monitor's "Save" gate on this, and both flush the
// config field's normalisation inside the same click — so without the `problem`
// clause a target the field has already marked in red would persist anyway. The
// PATCH route does not re-validate, so an invalid save persists silently and
// stops recomputing user_summary.
// `every`, not `some`: "Add source" inserts a blank target, and the backend
// rejects the whole document for one blank.
// What a submit gate needs ON TOP of the live target verdict it already holds.
// A panel computes `hasRejectedTarget` from the DISPLAYED value — the buffered
// text when there is one, otherwise the stored value — so it already knows
// whether every target on screen is usable. Re-deriving that here from the
// STORED value vetoes a target the person has already corrected: "Add source"
// commits a blank, the typed replacement is only buffered, and the gate kept
// reading the blank, so Start monitoring and Save stayed disabled with no
// explanation until the panel was closed and reopened.
export const isMonitorShapeComplete = (
	content: Pick<CustomMonitorContent, 'sources' | 'instruction'>,
): boolean => {
	const sources = content.sources ?? [];
	return (
		sources.length >= 1 &&
		sources.length <= MAX_MONITOR_SOURCES &&
		monitorTrim(content.instruction).length > 0
	);
};

// The stored targets, judged on their own. A panel seeds its live verdict with
// this so the gate is closed on a blank or a page BEFORE MonitorConfigBody has
// reported anything. Seeded false, the gate would stand open on first render.
export const hasRejectedStoredTarget = (
	content: Pick<CustomMonitorContent, 'sources'>,
): boolean => {
	const sources = content.sources ?? [];
	return sources.some(
		(s) =>
			monitorTrim(s?.target).length === 0 ||
			Boolean(checkMonitorTarget(s.tool, s.target).problem),
	);
};

// The whole-document rule, for a caller holding no live panel state.
export const isMonitorContentComplete = (
	content: Pick<CustomMonitorContent, 'sources' | 'instruction'>,
): boolean => {
	const sources = content.sources ?? [];
	return (
		sources.length >= 1 &&
		sources.length <= MAX_MONITOR_SOURCES &&
		sources.every((s) => {
			if (monitorTrim(s?.target).length === 0) return false;
			return !checkMonitorTarget(s.tool, s.target).problem;
		}) &&
		monitorTrim(content.instruction).length > 0
	);
};

// Live plain-language preview of what the monitor will do. Its Python twin
// (summary_sentence) is the persistence authority for `user_summary`, and the
// user reads this preview while configuring and that sentence in the Jobs row
// afterwards — so the two MUST produce identical output. The shared golden at
// services/py/shared/agents/tests/fixtures/monitor_summary_parity.json pins both.
// Stays deterministic and never touches the clock.
export const monitorSummarySentence = (
	content: Pick<
		CustomMonitorContent,
		'sources' | 'instruction' | 'cadence' | 'output_mode'
	>,
): string => {
	const targets = (content.sources ?? [])
		.map((s) => monitorTrim(s?.target))
		.filter((t) => t.length > 0);
	const targetPhrase =
		targets.length === 0
			? 'your sources'
			: targets.length === 1
				? targets[0]
				: `${targets.slice(0, -1).join(', ')} and ${targets[targets.length - 1]}`;
	const instruction = monitorTrim(content.instruction);
	const conditionPhrase = instruction ? `, ${instruction}` : '';
	return `${cadencePhrase(content.cadence)}, check ${targetPhrase}${conditionPhrase}; then ${outputClause(
		content.output_mode,
	)}.`;
};

// Per-tool guidance for the "what to watch" field. Each sensing tool wants a
// different shape, and the field previously showed one website placeholder for all
// four — so picking Subreddit and pasting a reddit URL looked right and silently
// failed.
export const SOURCE_TOOL_HINTS: Record<
	MonitorSource['tool'],
	{ placeholder: string; help: string }
> = {
	scrape_website: {
		placeholder: 'yourwebsite.com/pricing',
		help: 'A page address. Paste the full link if you have it.',
	},
	web_search: {
		placeholder: 'best project management tools 2026',
		help: 'What you would type into a search box.',
	},
	reddit_top_posts: {
		placeholder: 'marketing',
		help: 'A subreddit name. Paste a reddit link and we will pull the name out.',
	},
	scrape_instagram: {
		placeholder: 'adobe',
		help: 'An account handle. Paste a profile link and we will pull the handle out.',
	},
};

// What a target normalises to for a given tool, and whether it is usable.
export interface TargetCheck {
	/** The value to persist — cleaned up, not merely trimmed. */
	value: string;
	/** Shown when we changed what the owner typed, so the change is never silent. */
	note?: string;
	/** Shown when we cannot make the value usable. */
	problem?: string;
}

const IG_HOST = /^(?:https?:\/\/)?(?:www\.)?instagram\.com\//i;
const REDDIT_HOST = /^(?:https?:\/\/)?(?:www\.|old\.)?reddit\.com\//i;

// Instagram uses these first path segments for its own pages, not for accounts.
// A tags page, a post link, a reel and a story all look like a profile link at
// first glance, so taking the first segment as a handle turns
// instagram.com/explore/tags/interiordesign into the account "explore" — a name
// nobody owns, which the scraper then asks for on every run, forever.
const IG_RESERVED_PATHS = new Set([
	'p',
	'reel',
	'reels',
	'tv',
	'stories',
	'explore',
	's',
	'accounts',
	'direct',
	'about',
	'developer',
	'legal',
	'api',
]);

/**
 * Turn what a person typed into what the sensing tool actually expects.
 *
 * The tools are strict in ways the field never surfaced: `scrape_instagram` wants a
 * bare handle ("not a URL, not a post link") and `reddit_top_posts` passes its input
 * straight through to the Apify actor. So pasting a profile or subreddit URL — the
 * most natural thing to do — produced a monitor that ran forever and found nothing.
 */
export const checkMonitorTarget = (
	tool: MonitorSource['tool'],
	raw: string,
): TargetCheck => {
	const input = monitorTrim(raw);
	if (!input) return { value: '' };

	if (tool === 'scrape_instagram') {
		const isUrl = IG_HOST.test(input);
		const segment = isUrl
			? (input.replace(IG_HOST, '').split(/[/?#]/)[0] ?? '')
			: input.replace(/^@/, '');
		if (isUrl && !segment) {
			return { value: input, problem: 'That link has no account in it.' };
		}
		// Checked ONCE, on the extracted segment, so both paths are covered.
		// Inside the URL branch it missed a bare handle, and typing "explore" is
		// at least as natural as pasting the link (ADO-1890).
		if (IG_RESERVED_PATHS.has(segment.toLowerCase())) {
			// Hand back the text unchanged, not an empty string. The config field
			// flushes this value into the document on the same click as Save, so an
			// empty value would wipe a target that used to work. Unchanged text also
			// keeps the box showing what the person pasted, under the red line.
			return {
				value: input,
				problem:
					"That is an Instagram page, not a business account. Paste the account's profile link, like instagram.com/adobe.",
			};
		}
		if (isUrl) {
			return { value: segment, note: `Watching @${segment}` };
		}
		const handle = segment;
		if (/[/\s]/.test(handle)) {
			return {
				value: handle,
				problem: 'Use just the account handle, like adobe.',
			};
		}
		return handle === input
			? { value: handle }
			: { value: handle, note: `Watching @${handle}` };
	}

	if (tool === 'reddit_top_posts') {
		if (REDDIT_HOST.test(input)) {
			const path = input.replace(REDDIT_HOST, '');
			const name = /^r\//i.test(path) ? path.slice(2).split(/[/?#]/)[0] : '';
			if (!name) {
				return { value: input, problem: 'That link has no subreddit in it.' };
			}
			return { value: name, note: `Watching r/${name}` };
		}
		const name = input.replace(/^\/?r\//i, '').replace(/\/$/, '');
		if (/[/\s]/.test(name)) {
			return {
				value: name,
				problem: 'Use just the subreddit name, like marketing.',
			};
		}
		return name === input
			? { value: name }
			: { value: name, note: `Watching r/${name}` };
	}

	if (tool === 'scrape_website') {
		if (/\s/.test(input)) {
			return {
				value: input,
				problem: 'That looks like a search, not a page address.',
			};
		}
		if (!/\./.test(input)) {
			return {
				value: input,
				problem: 'That does not look like a web address.',
			};
		}
		return { value: input };
	}

	return { value: input };
};
